import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Award,
  Building2,
  ChevronRight,
  Download,
  FileText,
  Filter,
  Home,
  Menu,
  Search,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useLocale from '../contexts/LocaleContext';
import { useFacilitiesRequirementStatus, useLocationRankingByObjective, useStandingsPerObjective } from '../hooks/useObjectiveAnalytics';
import { useFacilityTypes } from '../hooks/useFacilities';
import { apiClient, objectiveAnalyticsApi } from '../lib/api-client';
import type { FacilityRequirementStatusItemDto, LocationRankingItemDto, RequirementType } from '../types/api';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

// Color scheme for status visualization
const STATUS_COLORS: Record<string, string> = {
  'Available': '#10b981',
  'Not Available': '#ef4444',
  'Unspecified': '#94a3b8',
};

const ObjectiveAnalytics: React.FC = () => {
  const { dictionary } = useLocale();

  // Available objectives - Must match RequirementType enum in backend
  const OBJECTIVES: Array<{ value: RequirementType; label: string; description: string }> = [
    {
      value: 'STANDARD_TOILET',
      label: dictionary['objective_analytics.objective_standard_toilet'],
      description: dictionary['objective_analytics.objective_standard_toilet_desc'],
    },
    {
      value: 'STANDARD_HOUSE',
      label: dictionary['objective_analytics.objective_standard_house'],
      description: dictionary['objective_analytics.objective_standard_house_desc'],
    },
    {
      value: 'HANDWASHING_FACILITY',
      label: dictionary['objective_analytics.objective_handwashing'],
      description: dictionary['objective_analytics.objective_handwashing_desc'],
    },
    {
      value: 'CLEAN_WATER_FACILITY',
      label: dictionary['objective_analytics.objective_clean_water'],
      description: dictionary['objective_analytics.objective_clean_water_desc'],
    },
    {
      value: 'WASTE_MANAGEMENT_FACILITY',
      label: dictionary['objective_analytics.objective_waste_management'],
      description: dictionary['objective_analytics.objective_waste_management_desc'],
    },
  ];

  const { data: facilityTypes = [] } = useFacilityTypes();

  // State management
  const [selectedObjective, setSelectedObjective] = useState<RequirementType>('STANDARD_TOILET');
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState<string | null>(null);
  const [exportAllLoading, setExportAllLoading] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Filter state
  type PeriodMode = 'week' | 'month' | 'dateRange';
  const [periodMode, setPeriodMode] = useState<PeriodMode>('week');
  const [weekNumber, setWeekNumber] = useState<number>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const jan4 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d.getTime() - jan4.getTime()) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
  });
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<string>(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [facilityTypeId, setFacilityTypeId] = useState<string>('');
  const [facilitiesPage, setFacilitiesPage] = useState(1);
  const facilitiesLimit = 20;

  const periodParams = periodMode === 'dateRange'
    ? { startDate: startDate || undefined, endDate: endDate || undefined }
    : periodMode === 'month'
      ? { month }
      : { weekNumber, year };

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  // Get current location ID from breadcrumb
  const currentLocationId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : undefined;

  // Fetch data using React Query hooks - each loads independently
  const {
    data: standingsData,
    isLoading: standingsLoading,
    error: standingsError,
  } = useStandingsPerObjective({
    objective: selectedObjective,
    facilityTypeId: facilityTypeId || undefined,
    ...periodParams,
  });
  const {
    data: facilitiesData,
    isLoading: facilitiesLoading,
    error: facilitiesError,
  } = useFacilitiesRequirementStatus({
    objective: selectedObjective,
    locationId: currentLocationId,
    facilityTypeId: facilityTypeId || undefined,
    ...periodParams,
    page: facilitiesPage,
    limit: facilitiesLimit,
    search: searchTerm || undefined,
  });

  const {
    data: rankingsData,
    isLoading: rankingsLoading,
    error: rankingsError,
  } = useLocationRankingByObjective({
    objective: selectedObjective,
    parentLocationId: currentLocationId,
    facilityTypeId: facilityTypeId || undefined,
    weekNumber: periodMode === 'week' ? weekNumber : undefined,
    year: periodMode === 'week' ? year : undefined,
  });

  // Breadcrumb navigation handler
  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Return to national level
      setBreadcrumb([]);
    } else {
      // Navigate to specific level
      const newBreadcrumb = breadcrumb.slice(0, index + 1);
      setBreadcrumb(newBreadcrumb);
    }
  };

  // Location drill-down handler
  const handleLocationDrillDown = (locationId: string, locationName: string, locationType: string) => {
    setBreadcrumb(prev => [...prev, { id: locationId, name: locationName, type: locationType }]);
    setFacilitiesPage(1);
  };

  // Status color mapping
  const getStatusColor = (status: string): string => {
    const statusMap: Record<string, string> = {
      available: STATUS_COLORS['Available'],
      notAvailable: STATUS_COLORS['Not Available'],
      unspecified: STATUS_COLORS['Unspecified'],
    };
    return statusMap[status] || STATUS_COLORS['Unspecified'];
  };

  // Status label mapping
  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      available: dictionary['objective_analytics.available'],
      notAvailable: dictionary['objective_analytics.not_available'],
      unspecified: dictionary['objective_analytics.unspecified'],
    };
    return statusMap[status] || status;
  };

  // Excel export handler — backend generates the .xlsx file
  const handleExportExcel = async () => {
    setExportLoading('excel');
    setShowExportDropdown(false);

    try {
      const params: Record<string, any> = {
        objective: selectedObjective,
        ...(currentLocationId && { locationId: currentLocationId }),
        ...(facilityTypeId && { facilityTypeId }),
        ...periodParams,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await apiClient.get('/api/v1/analytics/facilities-requirement-status/export', {
        params,
        responseType: 'blob',
      });

      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `objective-${selectedObjective.toLowerCase().replace(/_/g, '-')}-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Excel export failed:', error);
    } finally {
      setExportLoading(null);
    }
  };

  const handleExportPDF = () => {
    if (!facilitiesData?.data?.length) return;

    setExportLoading('pdf');
    setShowExportDropdown(false);

    try {
      const doc = new jsPDF();
      const margin = 20;
      let yPosition = 20;

      const currentObjectiveData = OBJECTIVES.find(obj => obj.value === selectedObjective);
      const locationContext =
        breadcrumb.length > 0
          ? breadcrumb[breadcrumb.length - 1].name
          : dictionary['objective_analytics.national_level'];

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(dictionary['objective_analytics.nhip'], margin, yPosition);
      yPosition += 10;

      // Subtitle
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(currentObjectiveData?.label || selectedObjective, margin, yPosition);
      yPosition += 8;
      doc.text(
        `${locationContext} | ${facilitiesData.total} ${dictionary['objective_analytics.facilities']}`,
        margin,
        yPosition
      );
      yPosition += 15;

      // Table
      const tableData = facilitiesData.data.map((facility: FacilityRequirementStatusItemDto) => [
        facility.name,
        facility.identifierNumber,
        facility.facilityTypeKinyarwanda,
        facility.location,
        getStatusLabel(facility.availabilityStatus),
        facility.assessmentWeek?.toString() || 'N/A',
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [
          [
            dictionary['objective_analytics.facility'],
            'ID',
            dictionary['objective_analytics.type'],
            dictionary['objective_analytics.location'],
            dictionary['objective_analytics.status'],
            dictionary['objective_analytics.week'],
          ],
        ],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246] },
      });

      const timestamp = new Date().toISOString().split('T')[0];
      doc.save(`Objective_Analytics_${selectedObjective}_${timestamp}.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setExportLoading(null);
    }
  };

  // Combined 5-objectives report — one row per facility, backend-generated .xlsx
  const handleExportAllObjectivesReport = async () => {
    setExportAllLoading(true);
    try {
      await objectiveAnalyticsApi.exportAllObjectivesExcel({
        ...(currentLocationId && { locationId: currentLocationId }),
        ...(facilityTypeId && { facilityTypeId }),
        ...periodParams,
        ...(searchTerm && { search: searchTerm }),
      });
    } catch (error) {
      console.error('Objectives report export failed:', error);
      toast.error(dictionary['objective_analytics.export_report_failed'] ?? 'Failed to download report');
    } finally {
      setExportAllLoading(false);
    }
  };

  // Search is server-side; just use the returned data directly
  const filteredFacilities = facilitiesData?.data ?? [];

  const currentObjectiveData = OBJECTIVES.find(obj => obj.value === selectedObjective);

  // Prepare pie chart data
  const pieChartData =
    standingsData?.data?.map(status => ({
      name: status.label,
      value: status.value,
      color: STATUS_COLORS[status.label],
    })) || [];

  // Custom label for pie chart
  const renderCustomLabel = (entry: any) => {
    const percent = standingsData?.total ? ((entry.value / standingsData.total) * 100).toFixed(1) : 0;
    return `${percent}%`;
  };

  return (
    <div className='bg-white rounded-lg shadow-sm overflow-x-hidden'>
      <div className='flex flex-col lg:flex-row gap-0 lg:gap-3'>
        {/* Sidebar Filters - Mobile: Fixed overlay, Desktop: Sidebar */}
        <div
          className={`${
            showFilters
              ? 'fixed inset-0 z-[100] lg:relative lg:w-56 lg:flex-shrink-0 lg:z-0'
              : 'hidden lg:block lg:w-56 lg:flex-shrink-0'
          } transition-all duration-300 lg:bg-gray-50 lg:rounded-lg lg:border lg:border-gray-200`}
        >
          {showFilters && (
            <div className='fixed inset-0 bg-black/50 lg:hidden z-[98]' onClick={() => setShowFilters(false)} />
          )}
          <div
            className={`${showFilters ? 'relative z-[99] bg-white h-full w-80 overflow-y-auto p-4 sm:p-6' : 'lg:p-3'}`}
          >
            <div className='flex items-center justify-between mb-4 lg:mb-4'>
              <h3 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2'>
                <Filter className='h-4 w-4 sm:h-5 sm:w-5' />
                {dictionary['objective_analytics.filters']}
              </h3>
              <button
                onClick={() => setShowFilters(false)}
                className='lg:hidden p-1 hover:bg-gray-100 rounded-lg transition-colors'
                aria-label='Close filters'
              >
                <X className='h-4 w-4' />
              </button>
            </div>

            <div className='space-y-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-3'>
                  {dictionary['objective_analytics.select_objective']}
                </label>
                <div className='space-y-3'>
                  {OBJECTIVES.map(obj => (
                    <label
                      key={obj.value}
                      className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedObjective === obj.value
                          ? 'border-primary bg-secondary'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type='radio'
                        name='objective'
                        value={obj.value}
                        checked={selectedObjective === obj.value}
                        onChange={e => {
                          setSelectedObjective(e.target.value as RequirementType);
                          setBreadcrumb([]);
                          setFacilitiesPage(1);
                          setShowFilters(false);
                        }}
                        className='mt-0.5 h-4 w-4 text-primary focus:ring-primary border-gray-300'
                      />
                      <div className='ml-3 flex-1'>
                        <span
                          className={`text-sm font-medium ${
                            selectedObjective === obj.value ? 'text-primary' : 'text-gray-900'
                          }`}
                        >
                          {obj.label}
                        </span>
                        <p className='text-xs text-gray-500 mt-1'>{obj.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* ── Period filter ── */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Period
                  <span className='ml-1 text-xs font-normal text-gray-400'>— which assessments to include</span>
                </label>
                <div className='flex gap-1 mb-2 bg-gray-100 rounded-lg p-1'>
                  {(['week', 'month', 'dateRange'] as const).map(mode => (
                    <button
                      key={mode}
                      onClick={() => setPeriodMode(mode)}
                      className={`flex-1 py-1 text-xs font-medium rounded-md transition-all ${periodMode === mode ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {mode === 'week' ? 'Week' : mode === 'month' ? 'Month' : 'Range'}
                    </button>
                  ))}
                </div>

                {periodMode === 'week' && (
                  <div className='flex gap-2'>
                    <div className='flex-1'>
                      <label className='block text-xs text-gray-500 mb-1'>
                        Week #
                        <span className='ml-1 text-gray-400'>1–53</span>
                      </label>
                      <Input
                        type='number' min={1} max={53}
                        value={weekNumber}
                        onChange={e => setWeekNumber(Number(e.target.value))}
                        className='text-sm'
                      />
                    </div>
                    <div className='flex-1'>
                      <label className='block text-xs text-gray-500 mb-1'>Year</label>
                      <Input
                        type='number' min={2020} max={2035}
                        value={year}
                        onChange={e => setYear(Number(e.target.value))}
                        className='text-sm'
                      />
                    </div>
                  </div>
                )}

                {periodMode === 'month' && (
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>
                      Month
                      <span className='ml-1 text-gray-400'>— all assessments in this month</span>
                    </label>
                    <Input
                      type='month'
                      value={month}
                      onChange={e => setMonth(e.target.value)}
                      className='text-sm'
                    />
                  </div>
                )}

                {periodMode === 'dateRange' && (
                  <div className='space-y-2'>
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>
                        Start date
                        <span className='ml-1 text-gray-400'>— inclusive</span>
                      </label>
                      <Input
                        type='date'
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        className='text-sm'
                      />
                    </div>
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>
                        End date
                        <span className='ml-1 text-gray-400'>— inclusive</span>
                      </label>
                      <Input
                        type='date'
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        className='text-sm'
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ── Facility type filter ── */}
              {facilityTypes.length > 0 && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Facility Type
                    <span className='ml-1 text-xs font-normal text-gray-400'>— narrow to one type</span>
                  </label>
                  <Input
                    type='select'
                    value={facilityTypeId}
                    onChange={e => setFacilityTypeId(e.target.value as string)}
                    placeholder='All facility types'
                    options={facilityTypes.map(ft => ({ value: ft.id, label: ft.name }))}
                    className='w-full text-sm'
                  />
                </div>
              )}

              {breadcrumb.length > 0 && (
                <Button variant='outline' onClick={() => setBreadcrumb([])} className='w-full'>
                  <X className='h-4 w-4 mr-2' />
                  {dictionary['objective_analytics.reset_location']}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='flex-1 min-w-0'>
          <div className='space-y-4 sm:space-y-6'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div className='flex-1 min-w-0'>
                <h2 className='text-base sm:text-xl font-semibold text-gray-900'>
                  {dictionary['objective_analytics.title']}
                </h2>
                <p className='text-xs sm:text-sm text-gray-600 mt-1 truncate'>
                  {dictionary['objective_analytics.performance_tracking']} {currentObjectiveData?.label}
                </p>
              </div>
              <div className='flex items-center gap-2 sm:gap-3 flex-shrink-0'>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className='lg:hidden flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
                >
                  <Menu className='h-3 w-3 sm:h-4 sm:w-4' />
                  <span>{dictionary['objective_analytics.filters']}</span>
                </button>
                <button
                  onClick={handleExportAllObjectivesReport}
                  disabled={exportAllLoading}
                  className='flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors text-primary hover:text-primary/80 hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  title={dictionary['objective_analytics.export_report'] ?? 'Download report: facilities × 5 key objectives'}
                >
                  <Download className='h-3 w-3 sm:h-4 sm:w-4' />
                  <span className='hidden sm:inline'>
                    {exportAllLoading
                      ? (dictionary['objective_analytics.exporting'] ?? 'Exporting…')
                      : (dictionary['objective_analytics.export_report'] ?? 'Download report')}
                  </span>
                  <span className='sm:hidden'>{exportAllLoading ? '...' : 'Report'}</span>
                </button>
                {facilitiesData && facilitiesData.data && facilitiesData.data.length > 0 && (
                  <div className='relative' ref={exportDropdownRef}>
                    <button
                      onClick={() => setShowExportDropdown(!showExportDropdown)}
                      className='flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-colors text-primary hover:text-primary/80 hover:bg-secondary'
                      disabled={exportLoading !== null}
                    >
                      <Download className='h-3 w-3 sm:h-4 sm:w-4' />
                      <span className='hidden sm:inline'>
                        {exportLoading
                          ? dictionary['objective_analytics.exporting']
                          : dictionary['objective_analytics.export']}
                      </span>
                      <span className='sm:hidden'>{exportLoading ? '...' : 'Export'}</span>
                    </button>

                    {/* Export Dropdown Menu */}
                    {showExportDropdown && (
                      <div className='absolute right-0 top-full mt-1 w-40 sm:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50'>
                        <div className='p-2'>
                          <button
                            onClick={handleExportExcel}
                            disabled={exportLoading !== null}
                            className='w-full flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-green-700 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                          >
                            <FileText className='h-3 w-3 sm:h-4 sm:w-4' />
                            <span>Excel</span>
                          </button>
                          <button
                            onClick={handleExportPDF}
                            disabled={exportLoading !== null}
                            className='w-full flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                          >
                            <FileText className='h-3 w-3 sm:h-4 sm:w-4' />
                            <span>PDF</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Breadcrumb Navigation */}
            {breadcrumb.length > 0 && (
              <div className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm bg-gray-50 p-2 sm:p-3 rounded-lg overflow-x-auto'>
                <button
                  onClick={() => handleBreadcrumbClick(-1)}
                  className='flex items-center gap-1 text-primary hover:text-primary/80 hover:underline whitespace-nowrap flex-shrink-0'
                >
                  <Home className='h-3 w-3 sm:h-4 sm:w-4' />
                  <span className='hidden sm:inline'>{dictionary['objective_analytics.national_level']}</span>
                  <span className='sm:hidden'>Home</span>
                </button>
                {breadcrumb.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ChevronRight className='h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0' />
                    {index === breadcrumb.length - 1 ? (
                      <span className='font-medium text-gray-900 whitespace-nowrap'>{item.name}</span>
                    ) : (
                      <button
                        onClick={() => handleBreadcrumbClick(index)}
                        className='text-primary hover:text-primary/80 hover:underline whitespace-nowrap'
                      >
                        {item.name}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Context Info */}
            <div className='bg-secondary p-4 rounded-lg border border-primary/30'>
              <div className='flex justify-between items-center'>
                <div>
                  <h3 className='text-base sm:text-lg font-semibold text-gray-900'>
                    {breadcrumb.length > 0
                      ? breadcrumb[breadcrumb.length - 1].name
                      : dictionary['objective_analytics.national_level']}
                  </h3>
                  <p className='text-xs sm:text-sm text-gray-600 mt-1'>
                    {currentObjectiveData?.label} • {standingsData?.total || 0}{' '}
                    {dictionary['objective_analytics.total_facilities_tracked']}
                    {' · '}
                    <span className='text-blue-600 font-medium'>
                      {periodMode === 'month'
                        ? month
                        : periodMode === 'dateRange'
                          ? `${startDate || '…'} → ${endDate || '…'}`
                          : `Week ${weekNumber}, ${year}`}
                    </span>
                    {facilityTypeId && (
                      <span className='ml-1 text-gray-400'>
                        · {facilityTypes.find(ft => ft.id === facilityTypeId)?.name ?? ''}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Overview Cards */}
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4'>
              {standingsLoading ? (
                // Skeleton loader for overview cards
                <>
                  {[...Array(4)].map((_, index) => (
                    <Card key={index} className='animate-pulse w-full'>
                      <CardContent className='p-4 sm:p-6'>
                        <div className='flex items-center justify-between'>
                          <div className='flex-1'>
                            <div className='h-4 bg-gray-200 rounded w-24 mb-3'></div>
                            <div className='h-8 bg-gray-200 rounded w-16 mb-2'></div>
                            <div className='flex items-center gap-2'>
                              <div className='h-2 bg-gray-200 rounded w-20'></div>
                              <div className='h-3 bg-gray-200 rounded w-10'></div>
                            </div>
                          </div>
                          <div className='bg-gray-200 p-3 rounded-full'>
                            <div className='h-8 w-8'></div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : standingsError ? (
                // Error state
                <Card className='col-span-full w-full'>
                  <CardContent className='p-6 text-center'>
                    <Target className='h-8 w-8 mx-auto mb-2 text-red-400' />
                    <p className='text-red-600 text-sm'>{dictionary['objective_analytics.error_loading_overview']}</p>
                  </CardContent>
                </Card>
              ) : (
                // Data loaded successfully
                <>
                  <Card className='hover:shadow-lg transition-shadow w-full'>
                    <CardContent className='p-4 sm:p-6'>
                      <div className='flex items-center justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <p className='text-xs sm:text-sm text-gray-600 mb-1'>
                            {dictionary['objective_analytics.total_facilities']}
                          </p>
                          <p className='text-2xl sm:text-3xl font-bold text-gray-900'>{standingsData?.total || 0}</p>
                        </div>
                        <div className='bg-secondary p-2 sm:p-3 rounded-full flex-shrink-0'>
                          <Building2 className='h-6 w-6 sm:h-8 sm:w-8 text-primary' />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className='hover:shadow-lg transition-shadow w-full'>
                    <CardContent className='p-4 sm:p-6'>
                      <div className='flex items-center justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <p className='text-xs sm:text-sm text-gray-600 mb-1'>Facilities inspected</p>
                          <p className='text-2xl sm:text-3xl font-bold text-gray-900'>{standingsData?.totalAssessedFacilities ?? 0}</p>
                        </div>
                        <div className='bg-green-100 p-2 sm:p-3 rounded-full flex-shrink-0'>
                          <Building2 className='h-6 w-6 sm:h-8 sm:w-8 text-green-600' />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {standingsData?.data?.map(status => (
                    <Card key={status.label} className='hover:shadow-lg transition-shadow w-full'>
                      <CardContent className='p-4 sm:p-6'>
                        <div className='flex items-center justify-between gap-3'>
                          <div className='flex-1 min-w-0'>
                            <p className='text-xs sm:text-sm text-gray-600 mb-1 truncate'>{status.label}</p>
                            <p
                              className='text-2xl sm:text-3xl font-bold'
                              style={{ color: STATUS_COLORS[status.label] }}
                            >
                              {status.value}
                            </p>
                            <div className='flex items-center gap-2 mt-2'>
                              <div className='flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]'>
                                <div
                                  className='h-2 rounded-full transition-all duration-500'
                                  style={{
                                    width: `${standingsData.total > 0 ? (status.value / standingsData.total) * 100 : 0}%`,
                                    backgroundColor: STATUS_COLORS[status.label],
                                  }}
                                />
                              </div>
                              <span className='text-xs font-medium text-gray-700 flex-shrink-0'>
                                {standingsData.total > 0 ? ((status.value / standingsData.total) * 100).toFixed(1) : 0}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </div>

            {/* Charts Section - Pie Chart and Bar Chart */}
            <div className='grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6'>
              {standingsLoading ? (
                // Skeleton loaders for charts
                <>
                  {[...Array(2)].map((_, index) => (
                    <Card key={index} className='animate-pulse'>
                      <CardHeader>
                        <div className='h-5 bg-gray-200 rounded w-40 mb-2'></div>
                        <div className='h-4 bg-gray-200 rounded w-56'></div>
                      </CardHeader>
                      <CardContent>
                        <div className='h-[300px] bg-gray-100 rounded flex items-center justify-center'>
                          <div className='text-gray-400'>{dictionary['objective_analytics.loading_chart']}</div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : standingsError ? (
                // Error state
                <Card className='col-span-2'>
                  <CardContent className='p-6 text-center'>
                    <Target className='h-8 w-8 mx-auto mb-2 text-red-400' />
                    <p className='text-red-600 text-sm'>{dictionary['objective_analytics.error_loading_charts']}</p>
                  </CardContent>
                </Card>
              ) : (
                // Charts loaded successfully
                <>
                  {/* Pie Chart */}
                  <Card className='hover:shadow-lg transition-shadow overflow-hidden'>
                    <CardHeader>
                      <CardTitle className='flex items-center gap-2'>
                        <Target className='h-5 w-5 text-primary' />
                        {dictionary['objective_analytics.status_distribution']}
                      </CardTitle>
                      <CardDescription>{dictionary['objective_analytics.overall_breakdown']}</CardDescription>
                    </CardHeader>
                    <CardContent className='overflow-hidden'>
                      <ResponsiveContainer width='100%' height={300}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx='50%'
                            cy='50%'
                            labelLine={false}
                            label={renderCustomLabel}
                            outerRadius={100}
                            fill='#8884d8'
                            dataKey='value'
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => [`${value} facilities`, 'Count']}
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            }}
                          />
                          <Legend
                            verticalAlign='bottom'
                            height={36}
                            iconType='circle'
                            formatter={value => <span className='text-sm text-gray-700'>{value}</span>}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Bar Chart */}
                  <Card className='hover:shadow-lg transition-shadow overflow-hidden'>
                    <CardHeader>
                      <CardTitle className='flex items-center gap-2'>
                        <TrendingUp className='h-5 w-5 text-primary' />
                        {dictionary['objective_analytics.facility_counts']}
                      </CardTitle>
                      <CardDescription>{dictionary['objective_analytics.number_by_status']}</CardDescription>
                    </CardHeader>
                    <CardContent className='overflow-hidden'>
                      <ResponsiveContainer width='100%' height={300}>
                        <BarChart
                          data={standingsData?.data || []}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                          <XAxis
                            dataKey='label'
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            axisLine={{ stroke: '#d1d5db' }}
                          />
                          <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#d1d5db' }} />
                          <Tooltip
                            cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            }}
                            formatter={(value: number) => [`${value} facilities`, 'Count']}
                          />
                          <Bar dataKey='value' radius={[8, 8, 0, 0]} maxBarSize={80}>
                            {standingsData?.data?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.label]} opacity={0.9} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Location Rankings */}
            {rankingsLoading ? (
              // Skeleton loader for location rankings
              <Card className='animate-pulse w-full'>
                <CardHeader className='pb-3'>
                  <div className='h-5 bg-gray-200 rounded w-48 mb-2'></div>
                  <div className='h-4 bg-gray-200 rounded w-64'></div>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    {[...Array(5)].map((_, index) => (
                      <div key={index} className='flex items-center justify-between p-4 border-b border-gray-100'>
                        <div className='flex items-center gap-4 flex-1'>
                          <div className='h-8 w-8 bg-gray-200 rounded-full'></div>
                          <div>
                            <div className='h-4 bg-gray-200 rounded w-32 mb-2'></div>
                            <div className='h-3 bg-gray-200 rounded w-20'></div>
                          </div>
                        </div>
                        <div className='flex gap-8'>
                          <div className='h-4 bg-gray-200 rounded w-12'></div>
                          <div className='h-4 bg-gray-200 rounded w-12'></div>
                          <div className='h-4 bg-gray-200 rounded w-12'></div>
                          <div className='h-4 bg-gray-200 rounded w-20'></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : rankingsError ? (
              // Error state
              <Card className='w-full'>
                <CardContent className='p-6 text-center'>
                  <Award className='h-8 w-8 mx-auto mb-2 text-red-400' />
                  <p className='text-red-600 text-sm'>{dictionary['objective_analytics.error_loading_rankings']}</p>
                </CardContent>
              </Card>
            ) : rankingsData?.data && rankingsData.data.length > 0 ? (
              // Data loaded successfully
              <Card className='hover:shadow-lg transition-shadow w-full'>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-base sm:text-lg'>
                    <Award className='h-4 w-4 sm:h-5 sm:w-5 text-primary' />
                    {dictionary['objective_analytics.location_rankings']}
                  </CardTitle>
                  <CardDescription className='text-xs sm:text-sm'>
                    {dictionary['objective_analytics.click_to_drill']} • {rankingsData.data.length}{' '}
                    {dictionary['objective_analytics.locations']}
                  </CardDescription>
                </CardHeader>
                <CardContent className='p-0 sm:p-6'>
                  <div className='overflow-x-auto'>
                    <table className='w-full min-w-[600px]'>
                      <thead>
                        <tr className='border-b border-gray-200'>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.rank']}
                          </th>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.location']}
                          </th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.total']}
                          </th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.available']}
                          </th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.not_available']}
                          </th>
                          {/* <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.unspecified']}
                          </th> */}
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.compliance']}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankingsData.data.map((location: LocationRankingItemDto) => (
                          <tr
                            key={location.locationId}
                            onClick={() =>
                              location.totalFacilities > 0 &&
                              handleLocationDrillDown(location.locationId, location.locationName, location.locationType)
                            }
                            className={`border-b border-gray-100 ${location.totalFacilities > 0 ? 'hover:bg-secondary cursor-pointer' : ''} transition-colors`}
                          >
                            <td className='py-2 sm:py-3 px-2 sm:px-4'>
                              <span
                                className={`inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-bold ${
                                  location.rank === 1
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : location.rank === 2
                                      ? 'bg-gray-200 text-gray-700'
                                      : location.rank === 3
                                        ? 'bg-orange-100 text-orange-800'
                                        : 'bg-secondary text-primary'
                                }`}
                              >
                                {location.rank}
                              </span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4'>
                              <div className='flex items-center gap-2'>
                                <div className='min-w-0'>
                                  <div className='text-xs sm:text-sm font-medium text-gray-900 truncate'>
                                    {location.locationName}
                                  </div>
                                  <div className='text-xs text-gray-500'>{location.locationType}</div>
                                </div>
                                {location.totalFacilities > 0 && (
                                  <ChevronRight className='h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0' />
                                )}
                              </div>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right text-xs sm:text-sm font-medium text-gray-900'>
                              {location.totalFacilities}
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right'>
                              <span className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800'>
                                {location.available}
                              </span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right'>
                              <span className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-800'>
                                {location.notAvailable}
                              </span>
                            </td>
                            {/* <td className='py-2 sm:py-3 px-2 sm:px-4 text-right'>
                              <span className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800'>
                                {location.unspecified}
                              </span>
                            </td> */}
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right'>
                              <div className='flex items-center justify-end gap-2'>
                                <div className='w-16 sm:w-20 bg-gray-200 rounded-full h-2 sm:h-2.5 flex-shrink-0'>
                                  <div
                                    className={`h-2 sm:h-2.5 rounded-full transition-all duration-500 ${
                                      location.availabilityPercentage >= 80
                                        ? 'bg-green-500'
                                        : location.availabilityPercentage >= 60
                                          ? 'bg-yellow-500'
                                          : location.availabilityPercentage >= 40
                                            ? 'bg-orange-500'
                                            : 'bg-red-500'
                                    }`}
                                    style={{ width: `${Math.min(location.availabilityPercentage, 100)}%` }}
                                  />
                                </div>
                                <span
                                  className={`text-xs sm:text-sm font-bold min-w-[45px] sm:min-w-[50px] ${
                                    location.availabilityPercentage >= 80
                                      ? 'text-green-600'
                                      : location.availabilityPercentage >= 60
                                        ? 'text-yellow-600'
                                        : location.availabilityPercentage >= 40
                                          ? 'text-orange-600'
                                          : 'text-red-600'
                                  }`}
                                >
                                  {location.availabilityPercentage.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Facilities Table */}
            <Card className='hover:shadow-lg transition-shadow w-full'>
              <CardHeader className='pb-3'>
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                  <div className='flex-1 min-w-0'>
                    <CardTitle className='flex items-center gap-2 text-base sm:text-lg'>
                      <Building2 className='h-4 w-4 sm:h-5 sm:w-5 text-primary' />
                      {dictionary['objective_analytics.facility_details']}
                    </CardTitle>
                    <CardDescription className='text-xs sm:text-sm mt-1'>
                      {facilitiesLoading ? (
                        <span className='inline-block h-4 bg-gray-200 rounded w-32 animate-pulse'></span>
                      ) : (
                        `${filteredFacilities.length} ${dictionary['objective_analytics.of']} ${facilitiesData?.total || 0} ${dictionary['objective_analytics.facilities']}`
                      )}
                    </CardDescription>
                  </div>
                  <div className='w-full sm:w-auto'>
                    <Input
                      type='text'
                      placeholder={dictionary['objective_analytics.search_facilities']}
                      value={searchTerm}
                      onChange={e => { setSearchTerm(e.target.value); setFacilitiesPage(1); }}
                      inputPrefix={<Search className='h-4 w-4 ml-3 text-gray-400 flex-shrink-0' />}
                      className='text-sm w-full sm:w-64'
                      disabled={facilitiesLoading}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className='p-0 sm:p-6'>
                {facilitiesLoading ? (
                  // Skeleton loader for facilities table
                  <div className='space-y-3 px-3 sm:px-0'>
                    {[...Array(6)].map((_, index) => (
                      <div key={index} className='flex items-center gap-4 p-3 border-b border-gray-100 animate-pulse'>
                        <div className='flex-1'>
                          <div className='h-4 bg-gray-200 rounded w-48 mb-2'></div>
                          <div className='h-3 bg-gray-200 rounded w-32'></div>
                        </div>
                        <div className='w-32'>
                          <div className='h-4 bg-gray-200 rounded w-24 mb-2'></div>
                          <div className='h-3 bg-gray-200 rounded w-20'></div>
                        </div>
                        <div className='w-32'>
                          <div className='h-4 bg-gray-200 rounded w-28 mb-2'></div>
                          <div className='h-3 bg-gray-200 rounded w-20'></div>
                        </div>
                        <div className='w-24'>
                          <div className='h-6 bg-gray-200 rounded-full w-20'></div>
                        </div>
                        <div className='w-24'>
                          <div className='h-4 bg-gray-200 rounded w-20'></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : facilitiesError ? (
                  // Error state
                  <div className='py-12 text-center px-3'>
                    <Building2 className='h-12 w-12 text-red-400 mx-auto mb-4' />
                    <p className='text-red-600 mb-2'>{dictionary['objective_analytics.error_loading_facilities']}</p>
                    <p className='text-sm text-gray-500'>{facilitiesError.message}</p>
                  </div>
                ) : (
                  // Data loaded successfully
                  <>
                  <div className='overflow-x-auto'>
                    <table className='w-full min-w-[550px]'>
                      <thead>
                        <tr className='border-b border-gray-200'>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.facility']}
                          </th>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.type']}
                          </th>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.location']}
                          </th>
                          <th className='text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.status']}
                          </th>
                          <th className='text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs uppercase whitespace-nowrap'>
                            {dictionary['objective_analytics.assessment']}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFacilities.length > 0 ? (
                          filteredFacilities.map((facility: FacilityRequirementStatusItemDto) => (
                            <tr
                              key={facility.id}
                              className='border-b border-gray-100 hover:bg-gray-50 transition-colors'
                            >
                              <td className='py-2 sm:py-3 px-2 sm:px-4'>
                                <div className='text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[150px] sm:max-w-none'>
                                  {facility.name}
                                </div>
                                <div className='text-xs text-gray-500 truncate'>{facility.identifierNumber}</div>
                              </td>
                              <td className='py-2 sm:py-3 px-2 sm:px-4'>
                                <div className='text-xs sm:text-sm text-gray-900 truncate max-w-[120px] sm:max-w-none'>
                                  {facility.facilityTypeKinyarwanda}
                                </div>
                                <div className='text-xs text-gray-500 truncate'>{facility.facilityType}</div>
                              </td>
                              <td className='py-2 sm:py-3 px-2 sm:px-4'>
                                <div className='text-xs sm:text-sm text-gray-900 truncate max-w-[120px] sm:max-w-none'>
                                  {facility.location}
                                </div>
                                <div className='text-xs text-gray-500 truncate'>{facility.locationType}</div>
                              </td>
                              <td className='py-2 sm:py-3 px-2 sm:px-4 text-center'>
                                <span
                                  className='inline-flex px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-semibold rounded-md whitespace-nowrap'
                                  style={{
                                    backgroundColor: `${getStatusColor(facility.availabilityStatus)}20`,
                                    color: getStatusColor(facility.availabilityStatus),
                                  }}
                                >
                                  {getStatusLabel(facility.availabilityStatus)}
                                </span>
                              </td>
                              <td className='py-2 sm:py-3 px-2 sm:px-4 text-center text-xs sm:text-sm text-gray-600'>
                                {facility.assessmentWeek ? (
                                  <span className='whitespace-nowrap'>
                                    {dictionary['objective_analytics.week']} {facility.assessmentWeek},{' '}
                                    {facility.assessmentYear}
                                  </span>
                                ) : (
                                  <span className='text-gray-400'>
                                    {dictionary['objective_analytics.not_assessed']}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className='py-12 text-center'>
                              <Building2 className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                              <p className='text-gray-600'>
                                {searchTerm
                                  ? dictionary['objective_analytics.no_facilities_found']
                                  : dictionary['objective_analytics.no_facilities_available']}
                              </p>
                              {searchTerm && (
                                <p className='text-sm text-gray-500 mt-2'>
                                  {dictionary['objective_analytics.try_adjusting']}
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {(facilitiesData as any)?.totalPages > 1 && (
                    <div className='flex items-center justify-between pt-3 border-t border-gray-100 mt-2'>
                      <span className='text-xs text-gray-500'>
                        Page {facilitiesPage} of {(facilitiesData as any).totalPages} · {facilitiesData?.total ?? 0} facilities
                      </span>
                      <div className='flex gap-2'>
                        <button
                          onClick={() => setFacilitiesPage(p => p - 1)}
                          disabled={facilitiesPage <= 1}
                          className='px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>
                          Previous
                        </button>
                        <button
                          onClick={() => setFacilitiesPage(p => p + 1)}
                          disabled={facilitiesPage >= (facilitiesData as any)?.totalPages}
                          className='px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveAnalytics;
